// Photo lightbox for article galleries: links with data-lightbox="<group>"
// open their full-size image in an overlay with previous/next, keyboard
// (← → Esc), swipe and a counter. Thumbnails under the main photo and the
// gallery tiles share one group, so each photo appears once in the set.
// Also expands a collapsed gallery ([data-gallery-toggle]).
//
// Ads beside the photo (Reklamy → Galeria, inc/ads.php): asked for when the
// viewer first opens, once per page view, and only those of the current
// screen are added — beside the photo on a computer, above it and below the
// buttons on a phone (none on a phone held sideways: the photo needs the
// height). They take their own grid cells, so they never cover the photo or
// the buttons; one that shows nothing collapses (src/js/ads.js).

import { mountAd, watchAds } from './ads.js';
import { DESKTOP, onMediaChange } from './media.js';

const TALL = '(min-height: 600px)'; // a phone's ads only with room for the photo (_art-live.scss)

export function initLightbox() {
  let box = null;
  let img;
  let caption;
  let counter;
  let prev;
  let next;
  let items = [];
  let index = 0;
  let lastFocus = null;
  let touchX = null;
  let ads = null; // markup by screen and place, once fetched: { d: { start, end }, m: { … } }
  let adsAsked = false;
  const desktop = window.matchMedia(DESKTOP);
  const tall = window.matchMedia(TALL);

  /** Add the current screen's ads that are not in the viewer yet (while it is open). */
  const placeAds = () => {
    if (!ads || !box || box.hidden) return;
    const screen = desktop.matches ? 'd' : tall.matches ? 'm' : '';
    for (const [pos, html] of Object.entries(ads[screen] || {})) {
      if (!html || box.querySelector(`.hp-lightbox__ad--${screen}.hp-lightbox__ad--${pos}`)) continue;
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      const el = tpl.content.firstElementChild;
      if (!el) continue;
      // Start before the "previous" button, end after "next": the order a keyboard goes through.
      if (pos === 'start') prev.before(el); else next.after(el);
      mountAd(el);
    }
    watchAds(box); // the ones added on an earlier opening are judged again, now that they show
  };

  const loadAds = () => {
    const url = window.PORTALE2026?.lightboxAds;
    if (adsAsked || !url || !window.fetch) return;
    adsAsked = true;
    fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => { ads = json && typeof json === 'object' ? json : null; placeAds(); })
      .catch(() => {}); // no ads: the viewer works as without them
  };
  onMediaChange(desktop, placeAds);
  onMediaChange(tall, placeAds);

  const build = () => {
    box = document.createElement('div');
    box.className = 'hp-lightbox';
    box.hidden = true;
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', 'Galeria zdjęć');
    box.innerHTML = `
      <button type="button" class="hp-lightbox__btn hp-lightbox__close" aria-label="Zamknij galerię">&times;</button>
      <button type="button" class="hp-lightbox__btn hp-lightbox__nav hp-lightbox__nav--prev" aria-label="Poprzednie zdjęcie">&#8249;</button>
      <figure class="hp-lightbox__figure">
        <img class="hp-lightbox__img" alt="">
        <figcaption class="hp-lightbox__caption"></figcaption>
      </figure>
      <button type="button" class="hp-lightbox__btn hp-lightbox__nav hp-lightbox__nav--next" aria-label="Następne zdjęcie">&#8250;</button>
      <p class="hp-lightbox__count" aria-live="polite"></p>`;
    document.body.append(box);

    img = box.querySelector('.hp-lightbox__img');
    caption = box.querySelector('.hp-lightbox__caption');
    counter = box.querySelector('.hp-lightbox__count');
    prev = box.querySelector('.hp-lightbox__nav--prev');
    next = box.querySelector('.hp-lightbox__nav--next');

    img.addEventListener('load', () => box.classList.remove('is-loading'));
    img.addEventListener('error', () => box.classList.remove('is-loading'));
    box.querySelector('.hp-lightbox__close').addEventListener('click', close);
    prev.addEventListener('click', () => show(index - 1));
    next.addEventListener('click', () => show(index + 1));
    box.addEventListener('click', (event) => {
      if (event.target === box || event.target.classList.contains('hp-lightbox__figure')) close();
    });
    box.addEventListener('touchstart', (event) => { touchX = event.touches[0].clientX; }, { passive: true });
    box.addEventListener('touchend', (event) => {
      if (touchX === null) return;
      const dx = event.changedTouches[0].clientX - touchX;
      touchX = null;
      if (Math.abs(dx) > 40) show(index + (dx < 0 ? 1 : -1));
    });
  };

  const preload = (i) => {
    const item = items[(i + items.length) % items.length];
    if (item) new Image().src = item.href;
  };

  const show = (i) => {
    index = (i + items.length) % items.length;
    const item = items[index];
    box.classList.add('is-loading');
    img.src = item.href;
    img.alt = item.alt;
    caption.textContent = item.caption;
    caption.hidden = !item.caption;
    counter.textContent = `${index + 1} / ${items.length}`;
    if (items.length > 1) {
      preload(index + 1);
      preload(index - 1);
    }
  };

  function close() {
    box.hidden = true;
    document.documentElement.classList.remove('has-lightbox');
    img.removeAttribute('src');
    if (lastFocus) lastFocus.focus();
  }

  const open = (link) => {
    if (!box) build();
    const seen = new Map();
    document.querySelectorAll(`[data-lightbox="${CSS.escape(link.dataset.lightbox)}"]`).forEach((a) => {
      if (!seen.has(a.href)) {
        seen.set(a.href, { href: a.href, alt: a.querySelector('img')?.alt || '', caption: a.dataset.caption || '' });
      }
    });
    items = [...seen.values()];
    lastFocus = link;
    const single = items.length < 2;
    prev.hidden = single;
    next.hidden = single;
    counter.hidden = single;
    box.hidden = false;
    document.documentElement.classList.add('has-lightbox');
    show(Math.max(0, items.findIndex((item) => item.href === link.href)));
    box.querySelector('.hp-lightbox__close').focus();
    if (ads) placeAds(); else loadAds();
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-gallery-toggle]');
    if (toggle) {
      const gallery = toggle.closest('[data-gallery]');
      const collapsed = gallery?.classList.toggle('is-collapsed');
      toggle.setAttribute('aria-expanded', String(!collapsed));
      toggle.textContent = collapsed ? toggle.dataset.labelMore : toggle.dataset.labelLess;
      if (collapsed) gallery?.scrollIntoView({ block: 'nearest', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      return;
    }

    let link = event.target.closest('a[data-lightbox]');
    // A photo linked from the content (WordPress "link to media file"): same viewer.
    if (!link) {
      const content = event.target.closest('.entry-content a, .hp-artl__body a, .hp-red__page a');
      if (content && /\.(jpe?g|png|gif|webp|avif)(\?|$)/i.test(content.getAttribute('href') || '')) {
        content.dataset.lightbox = content.dataset.lightbox || 'content';
        link = content;
      }
    }
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    open(link);
  });

  document.addEventListener('keydown', (event) => {
    if (!box || box.hidden) return;
    if (event.key === 'Escape') close();
    else if (event.key === 'ArrowLeft') show(index - 1);
    else if (event.key === 'ArrowRight') show(index + 1);
    else if (event.key === 'Tab') {
      // Keep focus inside the dialog: its buttons and the ads' links.
      const focusable = [...box.querySelectorAll('button:not([hidden]), a[href], iframe, [tabindex]:not([tabindex="-1"])')]
        .filter((el) => el.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });
}
